import { Control, FieldErrors } from 'react-hook-form';
import { Option, makeStyles, tokens } from '@fluentui/react-components';

import { ControlledField } from '@shared/components';

import {
  ASSESSOR_FUNCTION_OPTIONS,
  CHANGE_CATEGORY_OPTIONS,
  CRITICALITY_OPTIONS,
  SCOPE_TYPE_OPTIONS,
} from '../../constants';
import { CHANGE_MANAGER_CANDIDATES } from '../../data';
import { TRequestFormData } from '../../validation';
import { SectionCard } from '../shared';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
  },
  full: { gridColumn: '1 / -1' },
});

const identity = (value: string): string => value;

type TProps = {
  control: Control<TRequestFormData>;
  errors: FieldErrors<TRequestFormData>;
  readOnly: boolean;
};

type TSelectProps = TProps & {
  name: 'changeManager' | 'changeCategory' | 'scopeType' | 'criticality';
  label: string;
  options: string[];
};

const Select = ({ control, errors, readOnly, name, label, options }: TSelectProps) => (
  <ControlledField
    name={name}
    control={control}
    errors={errors}
    inputType="dropdown"
    label={label}
    readonly={readOnly}
    getLabel={identity}
    placeholder={`Select ${label.toLocaleLowerCase()}`}
  >
    {options.map((option) => (
      <Option key={option} value={option} text={option}>
        {option}
      </Option>
    ))}
  </ControlledField>
);

export const ChangeDetailsSection = ({ control, errors, readOnly }: TProps) => {
  const classes = useStyles();

  return (
    <SectionCard
      title="Change details"
      description="Assign ownership, classify the change and select the functions needed for assessment."
      collapsible
    >
      <div className={classes.grid}>
        <Select
          control={control}
          errors={errors}
          readOnly={readOnly}
          name="changeManager"
          label="Change manager"
          options={CHANGE_MANAGER_CANDIDATES}
        />
        <ControlledField
          name="targetDate"
          control={control}
          errors={errors}
          inputType="date"
          label="Target date"
          readonly={readOnly}
        />
        <Select
          control={control}
          errors={errors}
          readOnly={readOnly}
          name="changeCategory"
          label="Change category"
          options={CHANGE_CATEGORY_OPTIONS}
        />
        <Select
          control={control}
          errors={errors}
          readOnly={readOnly}
          name="scopeType"
          label="Scope"
          options={SCOPE_TYPE_OPTIONS}
        />
        <Select
          control={control}
          errors={errors}
          readOnly={readOnly}
          name="criticality"
          label="Criticality"
          options={CRITICALITY_OPTIONS}
        />
        <ControlledField
          name="assessorFunctions"
          control={control}
          errors={errors}
          inputType="dropdown"
          label="Assessor functions"
          readonly={readOnly}
          multiselect
          getLabel={identity}
          placeholder="Select assessor functions"
          fieldClassName={classes.full}
        >
          {ASSESSOR_FUNCTION_OPTIONS.map((assessorFunction) => (
            <Option key={assessorFunction} value={assessorFunction} text={assessorFunction}>
              {assessorFunction}
            </Option>
          ))}
        </ControlledField>
      </div>
    </SectionCard>
  );
};
